import {AnimatePresence, motion} from 'framer-motion'
import {toPng} from 'html-to-image'
import {useCallback, useEffect, useState, type RefObject} from 'react'
import {createPortal} from 'react-dom'

import type {Persona} from '../types'
import type {FraudGameShareType} from '../useFraudGameAnalytics'

export const ShareModal = ({
  isOpen,
  onClose,
  score,
  persona,
  rankPercent,
  cardRef,
  playerId,
  onShareClick,
}: {
  isOpen: boolean
  onClose: () => void
  score: number
  persona: Persona
  rankPercent: number
  cardRef: RefObject<HTMLDivElement | null>
  playerId: string
  onShareClick: (shareType: FraudGameShareType, shareUrl: string) => void
}) => {
  const [copied, setCopied] = useState(false)
  const [cardImage, setCardImage] = useState<string | null>(null)

  // Generate card image when modal opens
  useEffect(() => {
    if (!isOpen || !cardRef.current) {
      return
    }

    const el = cardRef.current

    const timer = setTimeout(() => {
      toPng(el, {pixelRatio: 2, cacheBust: true})
        .then(setCardImage)
        .catch(() => {
          setCardImage(null)
        })
    }, 50)

    return () => clearTimeout(timer)
  }, [isOpen, cardRef])

  // Lock body scroll when open
  useEffect(() => {
    if (isOpen) {
      const prev = document.body.style.overflow
      document.body.style.overflow = 'hidden'

      return () => {
        document.body.style.overflow = prev
      }
    }

    return undefined
  }, [isOpen])

  // Close on Escape
  useEffect(() => {
    if (!isOpen) {
      return undefined
    }

    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
      }
    }

    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [isOpen, onClose])

  const getShareUrl = useCallback(() => {
    if (typeof window === 'undefined') {
      return ''
    }

    const url = new URL(`${window.location.origin}${window.location.pathname}`)
    url.searchParams.set('ref', playerId)

    return url.toString()
  }, [playerId])

  const shareTitle = `Apparently I'm a ${persona.title} — what's your classification?`
  const shareText = `Apparently I'm a ${persona.title} — what's your classification?\nI took the Papertrails challenge and scored ${score} points (top ${rankPercent}%). 8 quick questions that test your financial instincts. Test yourself on getmoss.com/papertrails`

  const handleCopyLink = useCallback(async () => {
    const shareUrl = getShareUrl()

    try {
      await navigator.clipboard.writeText(shareUrl)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      const input = document.createElement('input')
      input.value = shareUrl
      document.body.appendChild(input)
      input.select()
      document.execCommand('copy')
      document.body.removeChild(input)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }

    onShareClick('copy_link', shareUrl)
  }, [getShareUrl, onShareClick])

  const handleLinkedIn = useCallback(() => {
    const shareUrl = getShareUrl()
    const url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`
    onShareClick('linkedin', shareUrl)
    window.open(url, '_blank', 'noopener,noreferrer')
  }, [getShareUrl, onShareClick])

  const handleWhatsApp = useCallback(() => {
    const shareUrl = getShareUrl()
    const text = encodeURIComponent(`${shareText}\n\n${shareUrl}`)
    onShareClick('whatsapp', shareUrl)
    window.open(`https://wa.me/?text=${text}`, '_blank', 'noopener,noreferrer')
  }, [getShareUrl, onShareClick, shareText])

  const handleEmail = useCallback(() => {
    const shareUrl = getShareUrl()
    const subject = encodeURIComponent(shareTitle)
    const body = encodeURIComponent(`${shareText}\n\nPlay here: ${shareUrl}`)
    onShareClick('email', shareUrl)
    window.location.href = `mailto:?subject=${subject}&body=${body}`
  }, [getShareUrl, onShareClick, shareText, shareTitle])

  const container = typeof document === 'undefined' ? null : document.getElementById('dialog-container')

  if (!container) {
    return null
  }

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center bg-moss-black/[70%] backdrop-blur-[10px]"
          initial={{opacity: 0}}
          animate={{opacity: 1}}
          exit={{opacity: 0}}
          transition={{duration: 0.25, ease: 'easeOut'}}
          onClick={onClose}
        >
          <motion.div
            className="relative mx-4 w-full max-w-[480px] rounded-2xl border border-grey-900 bg-[#171717] p-6 shadow-2xl"
            initial={{opacity: 0, y: 24, scale: 0.97}}
            animate={{opacity: 1, y: 0, scale: 1}}
            exit={{opacity: 0, y: 12, scale: 0.97}}
            transition={{type: 'spring', damping: 28, stiffness: 300, mass: 0.8}}
            onClick={(e) => e.stopPropagation()}
          >
            <style
              dangerouslySetInnerHTML={{
                __html: `
              @keyframes shimmer { 0% { background-position: 150% 0; } 100% { background-position: -150% 0; } }
              @keyframes sparkle {
                0%, 100% { opacity: 0; transform: scale(0); }
                50% { opacity: 1; transform: scale(1); }
              }
            `,
              }}
            />

            {/* Close button */}
            <button
              onClick={onClose}
              className="absolute right-4 top-4 flex size-8 items-center justify-center rounded-full text-grey-300 transition-colors hover:bg-grey-900 hover:text-grey-100"
              aria-label="Close share modal"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M12 4L4 12M4 4l8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
            </button>

            {/* Title */}
            <h2 className="mb-5 text-18 font-medium text-grey-110">Share your result</h2>

            {/* Card image preview */}
            <div className="mb-4 overflow-hidden bg-[#0a0a0a]">
              {cardImage ? (
                <img src={cardImage} alt="Your score card" className="block w-full" />
              ) : (
                <div className="flex aspect-[600/314] w-full items-center justify-center">
                  <div className="flex flex-col items-center gap-3 text-grey-450">
                    <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
                      <rect x="4" y="7" width="24" height="18" rx="3" stroke="currentColor" strokeWidth="1.5" />
                    </svg>
                    <span className="text-12">Generating preview…</span>
                  </div>
                </div>
              )}
            </div>

            {/* Share copy */}
            <div className="mb-6 px-3">
              <p className="text-14 font-medium text-grey-110">
                Apparently I&apos;m a {persona.title} — what&apos;s your classification?
              </p>
              <p className="mt-1 text-12 leading-5 text-grey-450">
                I took the Papertrails challenge and scored {score} points (top {rankPercent}%). 8 quick questions that
                test your financial instincts.
              </p>
            </div>

            {/* Share buttons row */}
            <div className="my-8 flex justify-center gap-8">
              <ShareButton label={copied ? 'Copied!' : 'Copy link'} onClick={handleCopyLink}>
                {copied ? (
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path
                      d="M4 10l4 4 8-8"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path
                      d="M8.5 11.5a3.5 3.5 0 005 0l2-2a3.5 3.5 0 00-5-5l-1 1"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                    />
                    <path
                      d="M11.5 8.5a3.5 3.5 0 00-5 0l-2 2a3.5 3.5 0 005 5l1-1"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                    />
                  </svg>
                )}
              </ShareButton>

              <ShareButton label="LinkedIn" onClick={handleLinkedIn}>
                <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M4.94 2.5A1.94 1.94 0 003 4.44c0 1.07.87 1.94 1.94 1.94h.02A1.94 1.94 0 006.9 4.44 1.94 1.94 0 004.94 2.5zM3.12 7.88h3.64v10.5H3.12V7.88zM10.87 7.88h-3.5v10.5h3.64v-5.85c0-1.55.7-2.6 2.06-2.6 1.13 0 1.58.86 1.58 2.12v6.33h3.64v-6.82c0-3.26-1.74-4.47-4.06-4.47-1.87 0-2.72.97-3.18 1.72l-.06.1h-.06l-.06-.1V7.88z" />
                </svg>
              </ShareButton>

              <ShareButton label="WhatsApp" onClick={handleWhatsApp}>
                <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M10 1.5A8.5 8.5 0 001.5 10c0 1.5.39 2.9 1.07 4.12L1.5 18.5l4.5-1.03A8.46 8.46 0 0010 18.5a8.5 8.5 0 000-17zm0 15.5a7 7 0 01-3.58-.98l-.26-.15-2.66.61.63-2.6-.17-.27A6.97 6.97 0 013 10a7 7 0 1114 0 7 7 0 01-7 7zm3.85-5.24c-.21-.11-1.25-.62-1.44-.69-.19-.07-.33-.1-.47.11-.14.21-.55.69-.67.83-.12.14-.25.16-.46.05-.21-.1-.9-.33-1.71-1.06-.63-.57-1.06-1.27-1.19-1.48-.12-.21-.01-.33.1-.43.1-.1.21-.25.32-.38.1-.13.14-.21.21-.36.07-.14.04-.27-.02-.38-.06-.1-.47-1.13-.64-1.55-.17-.41-.34-.35-.47-.36h-.4c-.14 0-.37.05-.56.27-.19.21-.74.72-.74 1.76s.76 2.04.86 2.18c.11.14 1.5 2.29 3.63 3.21.51.22.9.35 1.21.45.51.16.97.14 1.34.08.41-.06 1.25-.51 1.43-1 .18-.5.18-.92.13-1.01-.06-.09-.19-.14-.4-.25z" />
                </svg>
              </ShareButton>

              <ShareButton label="Email" onClick={handleEmail}>
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <rect x="2" y="4" width="16" height="12" rx="2" stroke="currentColor" strokeWidth="1.5" />
                  <path
                    d="M2 6l8 5 8-5"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </ShareButton>
            </div>

            {/* Prize banner */}
            <div
              className="relative flex items-center gap-4 overflow-hidden rounded-[11px] border-[1.4px] border-[#3d3c3c] p-3"
              style={{
                background: 'linear-gradient(144deg, rgba(16,16,16,0.5) 2.35%, rgba(52,52,52,0.5) 96.08%)',
                backdropFilter: 'blur(2.7px)',
              }}
            >
              {/* Shimmer sweep */}
              <div
                className="absolute inset-0"
                style={{
                  background:
                    'linear-gradient(110deg, transparent 0%, transparent 40%, rgba(255,255,255,0.04) 48%, rgba(255,255,255,0.06) 50%, rgba(255,255,255,0.04) 52%, transparent 60%, transparent 100%)',
                  backgroundSize: '250% 100%',
                  animation: 'shimmer 5s linear infinite',
                }}
              />
              <div className="relative size-16 shrink-0 overflow-hidden rounded-lg">
                <img
                  src="/images/fraud-game/prize-airpods.png"
                  alt="Apple AirPods"
                  className="size-full object-cover"
                />
              </div>
              <div className="relative flex flex-1 flex-col gap-0.5">
                <span className="text-14 font-medium text-grey-110">Unlock the sharer prize</span>
                <span className="text-12 leading-4 text-grey-300">
                  Share your result with others. The highest scoring sharer gets the chance to win Apple AirPods instead
                  of a Treatwell voucher.
                </span>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    container,
  )
}

const ShareButton = ({label, onClick, children}: {label: string; onClick: () => void; children: React.ReactNode}) => (
  <button onClick={onClick} className="group flex flex-col items-center gap-2" aria-label={label}>
    <span className="flex size-12 items-center justify-center rounded-full border border-grey-550 text-grey-110 transition-all group-hover:border-green-neon group-hover:text-green-neon">
      {children}
    </span>
    <span className="text-12 text-grey-110 transition-colors group-hover:text-green-neon">{label}</span>
  </button>
)
